import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import GlassCard from "@/components/ui/glass-card";
import { Achievement } from "@/hooks/useAchievementBadges";
import { achievementIcons } from "@/utils/achievement-icons";

interface AchievementBadgesProps {
  achievements: Achievement[];
  earnedCount: number;
  totalCount: number;
  initialBadgesCompleted: boolean;
}

const AchievementBadges = ({ 
  achievements, 
  earnedCount, 
  totalCount,
  initialBadgesCompleted
}: AchievementBadgesProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filter achievements based on whether initial badges are completed
  const visibleAchievements = initialBadgesCompleted 
    ? achievements 
    : achievements.filter(achievement => !achievement.isAdvanced);

  // Adjust the visible count
  const visibleTotalCount = initialBadgesCompleted ? totalCount : 8;

  return (
    <GlassCard 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full p-6"
      hover={false}
      variant="default"
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold">Achievement Badges 🏆</h2>
            <p className="text-muted-foreground">
              Track your capital raise milestones and progress
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="bg-white/50 dark:bg-gray-800/50 text-sm font-medium py-1.5 px-3">
              {earnedCount}/{visibleTotalCount} earned
            </Badge>
            <CollapsibleTrigger className="p-1 rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring">
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium">{isOpen ? "Hide" : "View"}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "transform rotate-180" : ""}`} />
              </div>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {visibleAchievements.map((achievement) => (
              <GlassCard 
                key={achievement.id}
                hover
                className={`p-6 ${achievement.earned ? "" : "opacity-70"}`}
                variant="subtle"
                shade={achievement.earned ? "medium" : "light"}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-full ${achievement.earned ? achievement.color : "bg-gray-200 dark:bg-gray-700"} flex items-center justify-center text-white mb-4`}>
                    {achievementIcons[achievement.id as keyof typeof achievementIcons]}
                  </div>
                  <h3 className="font-bold text-lg mb-1">{achievement.title}</h3>
                  <p className="text-sm text-muted-foreground">{achievement.description}</p>
                </div>
              </GlassCard>
            ))}
          </div>
          
          {initialBadgesCompleted && earnedCount === 8 && (
            <div className="mt-8 text-center">
              <GlassCard 
                hover
                className="p-6"
                variant="default"
                shade="medium"
              >
                <h3 className="font-bold text-xl mb-2">Achievement Unlocked: Advanced Badges!</h3>
                <p className="text-muted-foreground mb-4">
                  You've mastered the basics! Continue your fundraising journey to unlock advanced achievements.
                </p>
              </GlassCard>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </GlassCard>
  );
};

export default AchievementBadges;
